import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pdApi, type PdAttributeLevel, type PdProcessL2, type PdProcessL3 } from '../api';
import { OpportunityFormHelp, type OppTabKey } from './OpportunityFormHelp';
import { InfoTip } from '../InfoTip';
import '../screens.css';
import './OpportunityForm.css';
import { SelectField, PROMPT, UNSET, FIELD } from '@platform/ui-kit';

/**
 * Opportunity — create, edit and view.
 *
 * CLONED FROM `/Users/glucic/src/ai_readiness_template/views/opportunities-form.ejs`
 * (2,010 lines). Six tabs, the cards inside each, every label, helper line, placeholder,
 * character counter and empty-state string is the source's. Line references throughout
 * point back at that file so any of it can be checked rather than taken on trust.
 *
 * WHY THIS REPLACED THE PREVIOUS FORM. The first port of this screen read
 * `opportunities-edit.ejs` — a flat ten-card stack served by a DIFFERENT route — and
 * produced a 571-line screen of plain `pd-field` blocks. The screen the product actually
 * uses is this one, and the owner's verdict on the flat version was "completely ridiculous
 * layout". The cards, the tabs and the status band below are not a redesign; they are what
 * the source has always rendered.
 *
 * ── THE THREE MODES ─────────────────────────────────────────────────────────────────
 *
 *   create   no id. No status band, no Notes tab, no Submit/Delete. The Opportunity ID
 *            field appears only here (source :105) — it is the key, so it is set once.
 *   edit     id, writable.
 *   view     id, `readOnly`. Renders the SOURCE'S OWN read-only markup — the `<% } else { %>`
 *            branch of every card, the one it shows when `canFullEdit` is false. So the
 *            read-only screen is cloned too, not improvised.
 *
 * `readOnly` and `canFullEdit` are different things and both are honoured:
 *
 *   readOnly       the route is View. Nothing is writable, and neither Save, Delete,
 *                  Submit to Score nor unlink-from-project is offered at all.
 *   canFullEdit    the server's answer (source :6183) — false once an opportunity is In
 *                  Development or Delivered. Then ONLY the three document links can be
 *                  written, and the status band explains why.
 *
 * THE UNLINK-FROM-PROJECT CONTROL IS EDIT-ONLY, by the owner's instruction: "the function
 * of unlinking opportunity from project cannot be done in view mode only in edit mode."
 * It sits on the Ownership tab beside the project picker.
 *
 * ── WHAT DIFFERS FROM THE SOURCE ────────────────────────────────────────────────────
 *
 *   · The header, status band and tab strip do not scroll (CLAUDE.md, "A screen header
 *     never scrolls"). The source scrolls the page but already pins its own tab strip.
 *   · The page title uses `.pd-page-header`, the platform's heading, for consistency with
 *     every other screen in this module.
 *   · The two Financial-tab help links carry the platform's module prefix.
 *
 * All three are logged in docs/DEVIATIONS.md.
 */

/* ────────────────────────────────────────────────────────────────────────────────────
 * Form state — one key per field the source posts.
 * ──────────────────────────────────────────────────────────────────────────────────── */

interface FormState {
  opportunity_id_code: string;
  project_id: string;
  l1_id: string;
  l2_id: string;
  l3_id: string;
  l4: string;
  description: string;
  additional_comments: string;
  owner_name: string;
  business_process_owner: string;
  sponsor_keystakeholder: string;
  sponsor_keystakeholder_email: string;
  opportunity_owner_email: string;
  bpo_email: string;
  executive_owner: string;
  executive_owner_email: string;
  criticality_score: string;
  ai_alignment_score: string;
  benefit_cost_effectiveness_score: string;
  delivery_risk_score: string;
  mvp_value_strength_score: string;
  npv_value_strength: string;
  business_benefit: string;
  estimated_delivery_cost: string;
  opportunity_budget: string;
  opportunity_start_date: string;
  opportunity_end_date: string;
  initial_discussion_link: string;
  business_process_requirements_link: string;
  technical_requirements_link: string;
  /* Free-form classification. Source: opportunities-form.ejs:558, immediately before
     "Relevant for scoring?" — maxlength 100. */
  category: string;
  framework_relevant: string; // '1' | '0' — the source's select, not a checkbox
  is_mandatory: boolean;
  status: string;
  /* MVP financial parameters — these live on the default financial-model version, not on
     the opportunity row, but the source posts them with the rest of the form. */
  current_run_rate: string;
  future_run_rate: string;
  annual_hosting_cost: string;
  annual_support_cost: string;
}

const EMPTY: FormState = {
  opportunity_id_code: '',
  project_id: '',
  l1_id: '',
  l2_id: '',
  l3_id: '',
  l4: '',
  description: '',
  additional_comments: '',
  owner_name: '',
  business_process_owner: '',
  sponsor_keystakeholder: '',
  sponsor_keystakeholder_email: '',
  opportunity_owner_email: '',
  bpo_email: '',
  executive_owner: '',
  executive_owner_email: '',
  criticality_score: '',
  ai_alignment_score: '',
  benefit_cost_effectiveness_score: '',
  delivery_risk_score: '',
  mvp_value_strength_score: '',
  npv_value_strength: '',
  business_benefit: '',
  estimated_delivery_cost: '',
  opportunity_budget: '',
  opportunity_start_date: '',
  opportunity_end_date: '',
  initial_discussion_link: '',
  business_process_requirements_link: '',
  technical_requirements_link: '',
  category: '',
  // Source :143 — a NEW opportunity defaults to '0', "not relevant for scoring".
  framework_relevant: '0',
  is_mandatory: false,
  status: 'Draft',
  current_run_rate: '',
  future_run_rate: '',
  annual_hosting_cost: '',
  annual_support_cost: '',
};

type Dict = Record<string, unknown>;

/* ────────────────────────────────────────────────────────────────────────────────────
 * Formatting helpers — each matching the source's own output exactly.
 * ──────────────────────────────────────────────────────────────────────────────────── */

const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));

/** Source :610 — `'$' + Number(x).toLocaleString('en-US', {min:2, max:2})`. */
const money2 = (v: unknown) =>
  v === null || v === undefined || v === ''
    ? ''
    : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Source :917 — the read-only currency read-outs drop the cents. */
const money0 = (v: unknown) =>
  v === null || v === undefined || v === ''
    ? ''
    : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

/** Source :600 — `_ymd()`. */
const ymd = (v: unknown) => s(v).slice(0, 10);

/** Source :735 — the created/modified stamps. */
const stamp = (v: unknown) =>
  !v
    ? ''
    : new Date(String(v)).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

/**
 * Status → chip class.
 *
 * The EDIT view (:446–452) maps five statuses; the VIEW view
 * (`opportunities-details.ejs`:1657–1666) maps eight, adding Committed, In Development and
 * Delivered. The longer list is used for both, because the shorter one is a subset — an
 * edit screen simply never reaches the last three, so nothing changes for it.
 */
function statusClass(status: string): string {
  if (status === 'Approved') return 'approved';
  if (status === 'Ready for Scoring') return 'ready';
  if (status === 'Scoring in Progress') return 'progress';
  if (status === 'Scored') return 'scored';
  if (status === 'Committed') return 'committed';
  if (status === 'In Development') return 'development';
  if (status === 'Delivered') return 'delivered';
  return 'draft';
}

/** Source :1097 — the NPV strength labels, which are hard-coded there, not a framework. */
const NPV_LABELS: Record<string, string> = {
  '1': 'Very Low',
  '2': 'Low',
  '3': 'Moderate',
  '4': 'High',
  '5': 'Very High',
};

/** The tab strip — source :490–516. Icons are the source's HTML entities. */
const TABS: { key: OppTabKey; icon: string; label: string }[] = [
  { key: 'overview', icon: '\u{1F4C4}', label: 'Overview' },
  { key: 'ownership', icon: '\u{1F465}', label: 'Ownership' },
  { key: 'business', icon: '\u{1F4B0}', label: 'Business Value' },
  { key: 'financial', icon: '\u{1F4C8}', label: 'Financial Model' },
  { key: 'documents', icon: '\u{1F4C1}', label: 'Documentation' },
  { key: 'notes', icon: '\u{1F4DD}', label: 'Notes' },
  /* View only — `opportunities-details.ejs`:1706, icon &#128202;. */
  { key: 'scoring', icon: '\u{1F4CA}', label: 'Scoring Details' },
];

const REGISTRY = '/portfolio_definition/opportunities/opportunity-registry';

/* ────────────────────────────────────────────────────────────────────────────────────
 * Small presentational pieces, so the cards below read like the source's markup.
 * ──────────────────────────────────────────────────────────────────────────────────── */

function Card({ icon, title, tip, children }: {
  icon: string; title: string; tip?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <div className="opp-card">
      <div className="opp-card-content">
        <h3 className="opp-card-title">
          <i className="material-icons">{icon}</i>
          {title}
          {tip}
        </h3>
        {children}
      </div>
    </div>
  );
}

const Row = ({ children }: { children: React.ReactNode }) => (
  <div className="opp-form-row">{children}</div>
);

function Field({
  width,
  label,
  required,
  helper,
  children,
}: {
  /* `auto` sizes to its control, `rest` takes what is left — the pair used where a
     fixed-width control must sit next to a fluid field with the grid's normal gutter
     between them, rather than a percentage column that strands one from the other. */
  width: 'full' | 'half' | 'third' | 'quarter' | 'three-quarters' | 'auto' | 'rest';
  label?: React.ReactNode;
  required?: boolean;
  helper?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className={`opp-form-field opp-form-field-${width}`}>
      {label !== undefined ? (
        <label className="opp-form-label">
          {label}
          {required ? <span className="required"> *</span> : null}
        </label>
      ) : null}
      {children}
      {helper ? <div className="opp-form-helper">{helper}</div> : null}
    </div>
  );
}

/** A read-only value, with the source's `muted` treatment for an absent one. */
function ReadOnly({
  value,
  fallback = 'N/A',
  money,
  matchControl,
}: {
  value: React.ReactNode;
  fallback?: string;
  money?: boolean;
  /**
   * Sit at exactly the height of a SelectField beside it, for a row that pairs a picker
   * with the record it names (owner-directed 2026-09-27: "Project Description and Project
   * ID ... have to be of the same height").
   *
   * The metrics are READ FROM THE SAME TOKENS the control is built from rather than typed
   * here — a number copied out of `FIELD` would describe another element's size and drift
   * the day the token changes (CLAUDE.md §"Verify before reporting a fix").
   */
  matchControl?: boolean;
}) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div
      className={`opp-readonly-value${empty ? ' muted' : ''}${money ? ' money' : ''}`}
      style={
        matchControl
          ? {
              padding: FIELD.controlPadding,
              borderRadius: FIELD.controlRadius,
              fontSize: FIELD.controlFontSize,
              lineHeight: 1.45,
              minHeight: FIELD.controlHeight,
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
            }
          : undefined
      }
    >
      {empty ? fallback : value}
    </div>
  );
}

/**
 * A currency input. The source pairs a visible formatted text box with a hidden numeric
 * field (source :610–611) so the user sees "$1,234.00" while the form posts 1234. Here one
 * input does both: it shows the raw number while focused and the formatted value when not.
 */
function MoneyInput({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type="text"
      className="opp-form-input"
      placeholder="$0.00"
      disabled={disabled}
      value={focused ? value : money2(value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => onChange(e.target.value.replace(/[^0-9.-]/g, ''))}
    />
  );
}

/* ────────────────────────────────────────────────────────────────────────────────────
 * The screen.
 * ──────────────────────────────────────────────────────────────────────────────────── */

export function OpportunityForm({
  opportunityId,
  readOnly = false,
}: {
  opportunityId?: string;
  readOnly?: boolean;
}) {
  const navigate = useNavigate();
  const isNew = !opportunityId;

  const [form, setForm] = useState<FormState>({ ...EMPTY });
  const [opp, setOpp] = useState<Dict | null>(null);
  const [financialModel, setFinancialModel] = useState<Dict | null>(null);
  const [lookups, setLookups] = useState<Dict>({});
  const [l2, setL2] = useState<PdProcessL2[]>([]);
  const [l3, setL3] = useState<PdProcessL3[]>([]);
  const [canFullEdit, setCanFullEdit] = useState(true);

  /*
   * `?tab=scoring` opens the Scoring Details tab directly.
   *
   * The Completed scoring queue links here to SHOW a score without changing it — the owner's
   * ask: *"I need to have ability to see the scores w/o changing the scores."* Landing on
   * Overview and making him hunt for the tab would answer the letter of that and not the
   * point of it. Any other value, or none, keeps the normal Overview default.
   */
  const [tab, setTab] = useState<OppTabKey>(() => {
    const want = new URLSearchParams(window.location.search).get('tab');
    return TABS.some((t) => t.key === want) ? (want as OppTabKey) : 'overview';
  });
  const [helpOpen, setHelpOpen] = useState(false);

  const [notes, setNotes] = useState<Dict[] | null>(null);
  const [noteText, setNoteText] = useState('');

  /* Scoring results — only fetched on the view route, which is the only place the
     Scoring Details tab exists. Kept out of the edit path so editing does not pay for
     three queries it never renders. */
  const [dimensionScores, setDimensionScores] = useState<Dict[]>([]);
  const [questionScores, setQuestionScores] = useState<Dict[]>([]);
  const [recommendation, setRecommendation] = useState<Dict | null>(null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);

  /* ---- load ---- */

  /**
   * `keepEdits` re-reads the record but LEAVES THE FORM ALONE.
   *
   * Assigning a framework saves through its own endpoint and then reloads, and the reload
   * used to overwrite `form` from the freshly-read record — silently discarding a project
   * or status the user had picked but not yet saved. Reported 2026-09-27: "I assign the
   * project ... choose scoring framework, hit Update Opportunity, but project I selected
   * earlier does not get saved." It was never sent: the reload had already reset it.
   *
   * The framework picker reads `opp`, not `form`, so refreshing the record alone is enough
   * for it to show the new value.
   */
  const load = useCallback(async (keepEdits = false) => {
    setLoading(true);
    setError(null);
    try {
      if (isNew) {
        setLookups(await pdApi.opportunities.formData());
        return;
      }
      const data = (await pdApi.opportunities.edit(opportunityId!)) as Dict;
      setLookups(data);
      setCanFullEdit(Boolean(data.canFullEdit));
      setL2((data.l2_processes as PdProcessL2[]) ?? []);
      setL3((data.l3_processes as PdProcessL3[]) ?? []);
      setFinancialModel((data.financialModel as Dict) ?? null);

      const o = data.opportunity as Dict;
      const fm = (data.financialModel as Dict) ?? {};
      setOpp(o);
      if (keepEdits) return;
      setForm({
        opportunity_id_code: s(o.opportunity_id),
        project_id: s(o.project_id),
        l1_id: s(o.l1_id),
        l2_id: s(o.l2_id),
        l3_id: s(o.l3_id),
        l4: s(o.l4),
        description: s(o.description),
        additional_comments: s(o.additional_comments),
        owner_name: s(o.owner_name),
        business_process_owner: s(o.business_process_owner),
        sponsor_keystakeholder: s(o.sponsor_keystakeholder),
        sponsor_keystakeholder_email: s(o.sponsor_keystakeholder_email),
        opportunity_owner_email: s(o.opportunity_owner_email),
        bpo_email: s(o.bpo_email),
        executive_owner: s(o.executive_owner),
        executive_owner_email: s(o.executive_owner_email),
        criticality_score: s(o.criticality_score),
        ai_alignment_score: s(o.ai_alignment_score),
        benefit_cost_effectiveness_score: s(o.benefit_cost_effectiveness_score),
        delivery_risk_score: s(o.delivery_risk_score),
        mvp_value_strength_score: s(o.mvp_value_strength_score),
        npv_value_strength: s(o.npv_value_strength),
        business_benefit: s(o.business_benefit),
        estimated_delivery_cost: s(o.estimated_delivery_cost),
        opportunity_budget: s(o.opportunity_budget),
        opportunity_start_date: ymd(o.opportunity_start_date),
        opportunity_end_date: ymd(o.opportunity_end_date),
        initial_discussion_link: s(o.initial_discussion_link),
        business_process_requirements_link: s(o.business_process_requirements_link),
        technical_requirements_link: s(o.technical_requirements_link),
        category: s(o.category),
        // Source :144 — anything but an explicit `false` reads as relevant.
        framework_relevant: o.framework_relevant === false ? '0' : '1',
        is_mandatory: Boolean(Number(o.is_mandatory ?? 0)),
        status: s(o.status) || 'Draft',
        current_run_rate: s(fm.current_run_rate),
        future_run_rate: s(fm.future_run_rate),
        annual_hosting_cost: s(fm.annual_hosting_cost),
        annual_support_cost: s(fm.annual_support_cost),
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [isNew, opportunityId]);

  useEffect(() => {
    void load();
  }, [load]);


  /* Notes load with the screen, as they do in the source (its DOMContentLoaded handler). */
  const loadNotes = useCallback(async () => {
    if (isNew) return;
    try {
      setNotes((await pdApi.opportunities.notes(opportunityId!)) as Dict[]);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [isNew, opportunityId]);

  useEffect(() => {
    void loadNotes();
  }, [loadNotes]);

  const loadScoring = useCallback(async () => {
    if (isNew || !readOnly) return;
    try {
      const d = (await pdApi.opportunities.detail(opportunityId!)) as Dict;
      setDimensionScores((d.dimensionScores as Dict[]) ?? []);
      setQuestionScores((d.questionScores as Dict[]) ?? []);
      setRecommendation((d.recommendation as Dict) ?? null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [isNew, readOnly, opportunityId]);

  useEffect(() => {
    void loadScoring();
  }, [loadScoring]);

  /*
   * The locked shell's height. The platform shell never bounds its content area, so
   * `height: 100%` alone collapses; the height is measured from this element's top to the
   * bottom of the viewport and re-measured whenever the shell resizes.
   */
  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  /* ---- editing ---- */

  const set = (k: keyof FormState, v: string | boolean) => {
    setForm((f) => ({ ...f, [k]: v }));
    setSuccess(null);
  };

  /* Source :1520 — a new parent invalidates the child, so the child is cleared, not kept.
     An L3 under a different L2 is a different activity; silently keeping it would file the
     opportunity under a process it does not belong to. */
  const pickL1 = async (value: string) => {
    setForm((f) => ({ ...f, l1_id: value, l2_id: '', l3_id: '' }));
    setL3([]);
    setL2(value ? await pdApi.opportunities.l2ByL1(Number(value)) : []);
  };
  const pickL2 = async (value: string) => {
    setForm((f) => ({ ...f, l2_id: value, l3_id: '' }));
    setL3(value ? await pdApi.opportunities.l3ByL2(Number(value)) : []);
  };

  const body = () => ({
    ...form,
    l1_id: form.l1_id || null,
    l2_id: form.l2_id || null,
    l3_id: form.l3_id || null,
    project_id: form.project_id || null,
    is_mandatory: form.is_mandatory ? '1' : '0',
  });

  const save = async (): Promise<boolean> => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      if (isNew) {
        const r = await pdApi.opportunities.create(body());
        navigate(`${REGISTRY}?highlight=${encodeURIComponent(r.opportunity_id)}`);
      } else {
        await pdApi.opportunities.update(opportunityId!, body());
        // Source :6564 — "Opportunity updated successfully".
        setSuccess('Opportunity updated successfully');
        await load();
      }
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  /* Source :1678 — Submit to Score. If the status was changed to Approved but not saved,
     the source offers to save first and then send; that is reproduced. */
  const submitToScore = async () => {
    const savedStatus = s(opp?.status);
    if (form.status !== 'Approved') {
      setError('Opportunity must have Approved status to send to scoring');
      return;
    }
    if (savedStatus !== 'Approved') {
      if (
        !window.confirm(
          'The status has been changed to Approved but not saved yet.\n\nClick OK to save the changes and send to scoring, or Cancel to go back.',
        )
      )
        return;
      if (!(await save())) return;
    } else if (!window.confirm('Send this approved opportunity to scoring?')) {
      return;
    }
    setBusy(true);
    try {
      await pdApi.opportunities.sendToScoring(opportunityId!);
      navigate(REGISTRY);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /* Source :1661 — the delete confirmation, message text included. */
  const remove = async () => {
    const message =
      `Are you sure you want to delete this opportunity?\n\n` +
      `Opportunity ID: ${opportunityId}\n` +
      `Description: ${s(opp?.description).replace(/\n/g, ' ')}\n\n` +
      `This action cannot be undone.`;
    if (!window.confirm(message)) return;
    setBusy(true);
    try {
      await pdApi.opportunities.remove(opportunityId!);
      navigate(REGISTRY);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  /** The delivery project that owns this opportunity's dates, or '' while none does. */
  const inExecution = s(opp?.execution_delivery_project_id);

  /* Edit mode only, by the owner's instruction. */
  const unlinkProject = async () => {
    if (
      !window.confirm(
        `Remove this opportunity from project ${form.project_id}?\n\nThe opportunity is kept; only the link to the project is removed.`,
      )
    )
      return;
    setBusy(true);
    try {
      await pdApi.opportunities.unlinkProject(opportunityId!);
      await load();
      setSuccess('Opportunity removed from the project');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /* Carried over from the detail screen this one absorbed — see the note by the picker. */
  const assignFramework = async (frameworkId: string) => {
    setBusy(true);
    try {
      await pdApi.opportunities.assignFramework(opportunityId!, frameworkId);
      await load(true);
      setSuccess('Framework assigned');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /*
   * REVERT TO DRAFT. The source form DEFINES `revertToDraft()` (views/opportunities-form
   * .ejs:1712) but never renders a control that calls it — dead code there. The detail
   * screen this one absorbed did render it, so it is kept on the detail screen's own
   * condition (status is Approved) rather than dropped.
   */
  const revertToDraft = async () => {
    if (
      !window.confirm(
        'Revert this opportunity back to Draft status?\n\nThis will remove it from the scoring queue.',
      )
    )
      return;
    setBusy(true);
    try {
      await pdApi.opportunities.revertToDraft(opportunityId!);
      await load();
      setSuccess('Reverted to Draft.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setBusy(true);
    try {
      await pdApi.opportunities.addNote(opportunityId!, text);
      setNoteText('');
      await loadNotes();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const deleteNote = async (noteId: number) => {
    if (!window.confirm('Delete this note?')) return;
    setBusy(true);
    try {
      await pdApi.opportunities.deleteNote(opportunityId!, noteId);
      await loadNotes();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /* ---- derived ---- */

  const rubric = (key: string): PdAttributeLevel[] => (lookups[key] as PdAttributeLevel[]) ?? [];
  const projects = (lookups.projects as Dict[]) ?? [];
  const l1List = (lookups.l1_processes as Dict[]) ?? [];
  const availableFrameworks = (lookups.availableFrameworks as Dict[]) ?? [];
  const canUnlink = Boolean(lookups.canUnlink);

  /** `full` = the fields other than document links are writable. */
  const full = !readOnly && canFullEdit;
  /** Document links stay writable in limited-edit mode; View freezes them too. */
  const docsWritable = !readOnly;

  /* Source :356 — the picker's `data-description` attribute, resolved on change. A find
     over a few hundred projects each render is cheaper than memoising a value derived from
     a list that is itself rebuilt on every render. */
  const projectDescription = form.project_id
    ? s(projects.find((x) => s(x.project_id) === form.project_id)?.project_description)
    : '';

  const lookupName = (list: string, idField: string, nameField: string, id: unknown) => {
    const rows = (lookups[list] as Dict[]) ?? [];
    return s(rows.find((r) => s(r[idField]) === s(id))?.[nameField]);
  };

  /*
   * Which tabs exist:
   *   notes    once the opportunity exists (source :512 gates on `opportunity`)
   *   scoring  view route only, and only when there is something to show — the source's
   *            own gate, `opportunities-details.ejs`:1701.
   */
  /*
   * A PART-SCORED OPPORTUNITY SHOWS ITS ANSWERS TOO.
   *
   * The source's gate (`opportunities-details.ejs`:1701) hides this tab while an assessment
   * is still in progress, so the answers already given could not be read anywhere without
   * re-opening the questionnaire — and on the Scoring in Progress screen the only other
   * action is Continue, which is the one that changes them. The owner asked for the view on
   * BOTH screens (2026-09-30), so the exclusion is dropped: if there are answers, they can
   * be read. Dimension and overall scores simply do not exist yet, and the pane says so.
   *
   * A deviation from the source, logged in docs/DEVIATIONS.md.
   */
  const showScoring = readOnly && (dimensionScores.length > 0 || questionScores.length > 0);

  const tabs = TABS.filter((t) => {
    if (t.key === 'notes') return !isNew;
    if (t.key === 'scoring') return showScoring;
    return true;
  });

  if (loading) return <div className="pd-screen pd-loading">Loading…</div>;

  const heading = isNew ? 'Create New Opportunity' : readOnly ? 'View Opportunity' : 'Edit Opportunity';
  const subtitle = isNew ? 'Enter details for the new opportunity' : `Opportunity ${opportunityId}`;

  /* ────────────────────────────────────────────────────────────────────────────────
   * TAB 1 — OVERVIEW.  source :519–758
   * ──────────────────────────────────────────────────────────────────────────────── */
  const overviewPane = (
    <>
      <Card icon="description" title="Description & Comments">
        {full ? (
          <>
            {isNew ? (
              <Row>
                <Field
                  width="half"
                  label="Opportunity ID"
                  required
                  helper="Enter a unique Opportunity ID (any format)."
                >
                  <input
                    type="text"
                    className="opp-form-input"
                    maxLength={50}
                    placeholder="e.g., PRJ-LS-01-OPP-05"
                    value={form.opportunity_id_code}
                    onChange={(e) => set('opportunity_id_code', e.target.value)}
                  />
                </Field>
              </Row>
            ) : null}

            {/*
              STATUS LIVES ON OVERVIEW. Owner ruling, 2026-09-30: *"The status section should
              be moved to Overview section since it belongs there."* It sat inside the
              Stakeholders card on the Ownership tab, which is about WHO owns the
              opportunity, not where it has got to — and in View mode it was not rendered at
              all, so the one thing a reader looks for first could not be seen.
            */}
            {!isNew ? (
              <Row>
                <Field
                  width="half"
                  label="Status"
                  helper="Change status to Approved to enable sending to scoring"
                >
                  {/* Source :416 — only Draft and Approved are offered; a later status is
                      shown as a selected, disabled option because this form does not own
                      that transition. */}
                  <select
                    className="opp-form-select"
                    value={form.status}
                    onChange={(e) => set('status', e.target.value)}
                  >
                    <option value="Draft">Draft</option>
                    <option value="Approved">Approved</option>
                    {['Ready for Scoring', 'Scoring in Progress', 'Scored'].includes(
                      s(opp?.status),
                    ) ? (
                      <option value={s(opp?.status)} disabled>
                        {s(opp?.status)}
                      </option>
                    ) : null}
                  </select>
                </Field>
              </Row>
            ) : null}

            <Row>
              <Field width="full" label="Description" required>
                <textarea
                  className="opp-form-textarea"
                  maxLength={300}
                  placeholder="Enter opportunity description"
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                />
                <div className="opp-char-count">{form.description.length}/300 characters</div>
              </Field>
            </Row>

            <Row>
              <Field width="full" label="Additional Comments (Optional)">
                <textarea
                  className="opp-form-textarea"
                  maxLength={2000}
                  placeholder="Enter any additional comments or notes"
                  value={form.additional_comments}
                  onChange={(e) => set('additional_comments', e.target.value)}
                />
                <div className="opp-char-count">
                  {form.additional_comments.length}/2000 characters
                </div>
              </Field>
            </Row>

            {/* Source :558 — Category sits immediately before "Relevant for scoring?". */}
            <Row>
              <Field width="half" label="Category">
                <input
                  type="text"
                  className="opp-form-input"
                  maxLength={100}
                  placeholder="e.g. Reporting, Automation, Data Quality"
                  value={form.category}
                  onChange={(e) => set('category', e.target.value)}
                />
              </Field>
            </Row>

            <Row>
              <Field
                width="half"
                required
                label={
                  <>
                    Relevant for scoring?
                    {/* Source :572 — `partials/info-tip`, id oppScoringRel. Copy verbatim. */}
                    <InfoTip title="Relevant for scoring?">
                      <p>Controls whether this opportunity goes through <strong>scoring</strong>.</p>
                      <ul>
                        <li><strong>Yes</strong> — the opportunity follows the normal scoring flow (Approved → sent to scoring → Scored) using its project’s framework.</li>
                        <li><strong>No</strong> — the opportunity is <strong>exempt from scoring</strong>: it can’t be sent to scoring and it does <strong>not</strong> block its project from being marked Complete.</li>
                      </ul>
                      <p>This is decided <strong>per opportunity</strong> and defaults to No.</p>
                    </InfoTip>
                  </>
                }
              >
                <select
                  className="opp-form-select"
                  value={form.framework_relevant}
                  onChange={(e) => set('framework_relevant', e.target.value)}
                >
                  <option value="1">Yes — this opportunity will be scored</option>
                  <option value="0">No — not relevant for scoring</option>
                </select>
              </Field>
            </Row>
          </>
        ) : (
          <>
            {/* View shows Status too — it was absent from the read-only branch entirely. */}
            {!isNew ? (
              <Row>
                <Field width="half" label="Status">
                  <ReadOnly value={s(opp?.status)} />
                </Field>
              </Row>
            ) : null}
            <Row>
              <Field width="full" label="Description">
                <ReadOnly value={s(opp?.description)} />
              </Field>
            </Row>
            <Row>
              <Field width="full" label="Additional Comments">
                <ReadOnly
                  value={s(opp?.additional_comments)}
                  fallback="No additional comments"
                />
              </Field>
            </Row>
            <Row>
              <Field width="half" label="Category">
                <ReadOnly value={s(opp?.category)} />
              </Field>
            </Row>
            <Row>
              {/* View is the edit screen frozen (owner ruling): the same field, the same
                  "i", read-only (2026-09-21). */}
              <Field
                width="half"
                label={
                  <>
                    Relevant for scoring?
                    <InfoTip title="Relevant for scoring?">
                      <p>Controls whether this opportunity goes through <strong>scoring</strong>.</p>
                      <ul>
                        <li><strong>Yes</strong> — the opportunity follows the normal scoring flow (Approved → sent to scoring → Scored) using its project’s framework.</li>
                        <li><strong>No</strong> — the opportunity is <strong>exempt from scoring</strong>: it can’t be sent to scoring and it does <strong>not</strong> block its project from being marked Complete.</li>
                      </ul>
                      <p>This is decided <strong>per opportunity</strong> and defaults to No.</p>
                    </InfoTip>
                  </>
                }
              >
                <ReadOnly
                  value={
                    opp?.framework_relevant === false
                      ? 'No — not relevant for scoring'
                      : 'Yes — this opportunity will be scored'
                  }
                />
              </Field>
            </Row>
          </>
        )}
      </Card>

      <Card icon="savings" title="Opportunity Budget & Timeline">
        {full ? (
          <Row>
            <Field width="third" label="Opportunity Budget">
              <MoneyInput
                value={form.opportunity_budget}
                onChange={(v) => set('opportunity_budget', v)}
              />
            </Field>
            {/*
              THE DATES CHANGE HANDS WHEN A DELIVERY PROJECT IS CREATED (owner's rule).
              Until then this screen owns them and Project Creation reads them live; once
              the opportunity is in execution, Project Management owns them and writes them
              back here, so the boxes go read-only and say who holds them. Editing them here
              would not have reached `roadmap_execution`, and the two would have diverged.
            */}
            {inExecution ? (
              <>
                <Field
                  width="third"
                  label="Start Date"
                  helper={`Managed in Project Management & Execution (delivery project ${inExecution})`}
                >
                  <ReadOnly matchControl value={form.opportunity_start_date} fallback="Not set" />
                </Field>
                <Field
                  width="third"
                  label="End Date"
                  helper="Change it on Project Creation or Timeline Finalization; it is written back here."
                >
                  <ReadOnly matchControl value={form.opportunity_end_date} fallback="Not set" />
                </Field>
              </>
            ) : (
              <>
                <Field width="third" label="Start Date">
                  <input
                    type="date"
                    className="opp-form-input"
                    value={form.opportunity_start_date}
                    onChange={(e) => set('opportunity_start_date', e.target.value)}
                  />
                </Field>
                <Field width="third" label="End Date">
                  <input
                    type="date"
                    className="opp-form-input"
                    value={form.opportunity_end_date}
                    onChange={(e) => set('opportunity_end_date', e.target.value)}
                  />
                </Field>
              </>
            )}
          </Row>
        ) : (
          <Row>
            <Field width="third" label="Opportunity Budget">
              <ReadOnly value={money2(opp?.opportunity_budget)} fallback="Not set" />
            </Field>
            <Field width="third" label="Start Date">
              <ReadOnly value={ymd(opp?.opportunity_start_date)} fallback="Not set" />
            </Field>
            <Field width="third" label="End Date">
              <ReadOnly value={ymd(opp?.opportunity_end_date)} fallback="Not set" />
            </Field>
          </Row>
        )}
      </Card>

      <Card icon="account_tree" title="Process Hierarchy">
        {full ? (
          <>
            <Row>
              <Field width="third" label="L1 Process Group">
                <select
                  className="opp-form-select"
                  value={form.l1_id}
                  onChange={(e) => void pickL1(e.target.value)}
                >
                  <option value="">Select L1 Process</option>
                  {l1List.map((x) => (
                    <option key={s(x.l1_id)} value={s(x.l1_id)}>
                      {s(x.l1_name)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field width="third" label="L2 Process Area">
                <select
                  className="opp-form-select"
                  value={form.l2_id}
                  disabled={!form.l1_id}
                  onChange={(e) => void pickL2(e.target.value)}
                >
                  <option value="">Select L2 Process</option>
                  {l2.map((x) => (
                    <option key={x.l2_id} value={String(x.l2_id)}>
                      {x.l2_name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field width="third" label="L3 Activity">
                <select
                  className="opp-form-select"
                  value={form.l3_id}
                  disabled={!form.l2_id}
                  onChange={(e) => set('l3_id', e.target.value)}
                >
                  <option value="">Select L3 Activity</option>
                  {l3.map((x) => (
                    <option key={x.l3_id} value={String(x.l3_id)}>
                      {x.l3_name}
                    </option>
                  ))}
                </select>
              </Field>
            </Row>
            <Row>
              <Field width="full" label="L4 Sub-Activity (Optional)">
                <input
                  type="text"
                  className="opp-form-input"
                  maxLength={200}
                  placeholder="Enter L4 sub-activity if applicable"
                  value={form.l4}
                  onChange={(e) => set('l4', e.target.value)}
                />
              </Field>
            </Row>
          </>
        ) : (
          <>
            <Row>
              <Field width="quarter" label="L1 Process Group">
                <ReadOnly value={s(opp?.l1_name)} />
              </Field>
              <Field width="quarter" label="L2 Process Area">
                <ReadOnly value={s(opp?.l2_name)} />
              </Field>
              <Field width="quarter" label="L3 Activity">
                <ReadOnly value={s(opp?.l3_name)} />
              </Field>
              <Field width="quarter" label="L4 Sub-Activity">
                <ReadOnly value={s(opp?.l4)} />
              </Field>
            </Row>
            <Row>
              <Field width="half" label="Service Line">
                <ReadOnly
                  value={lookupName('service_lines', 'id', 'name', opp?.service_line_id)}
                />
              </Field>
              <Field width="half" label="Service Request">
                <ReadOnly
                  value={lookupName('service_requests', 'id', 'name', opp?.service_request_id)}
                />
              </Field>
            </Row>
          </>
        )}
      </Card>

      {/* Source :724 — creator / modifier, always read-only, only once the record exists. */}
      {!isNew ? (
        <Card icon="person" title="Creator / Modifier">
          <Row>
            <Field width="half" label="Created By">
              <ReadOnly value={s(opp?.created_by_name)} />
            </Field>
            <Field width="half" label="Created Date">
              <ReadOnly value={stamp(opp?.created_date)} />
            </Field>
          </Row>
          <Row>
            <Field width="half" label="Last Modified By">
              <ReadOnly value={s(opp?.modified_by_name)} fallback="Not modified yet" />
            </Field>
            <Field width="half" label="Last Modified Date">
              <ReadOnly value={stamp(opp?.modified_at)} fallback="Not modified yet" />
            </Field>
          </Row>
        </Card>
      ) : null}
    </>
  );

  /* ────────────────────────────────────────────────────────────────────────────────
   * TAB 2 — OWNERSHIP.  source :759–888
   * ──────────────────────────────────────────────────────────────────────────────── */
  const ownershipPane = (
    <>
      <Card icon="business" title="Project Assignment">
        {full ? (
          <Row>
            {/* No `label` on the Field: SelectField renders its own, so passing both would
                print the word twice.

                SIZED TO THE CONTROL, not to a percentage of the row (2026-09-27). The source's
                `<select>` shrank to its 25% column; the platform's SelectField is a FIXED 380px
                by owner ruling, and its root is an `inline-flex` sized by that child — so in a
                25% column (~250px) it painted straight over "Project Description" beside it.
                A 50% column fixed the overlap and then stranded the description 260px away.
                `auto` + `rest` give the field exactly its 380px and start the description after
                the grid's own 24px gutter. `maxWidth` below is the guard for a viewport too
                narrow for 380px: it clamps rather than overlapping. */}
            <Field width="auto" helper="Select project (optional)">
              <SelectField
                label="Project"
                orientation="stacked"
                width="lg"
                style={{ maxWidth: '100%' }}
                placeholder={PROMPT.project}
                value={s(form.project_id)}
                /* This field WRITES TO A RECORD, so UNSET is mapped back to '' — storing the
                   sentinel would put a literal `__unset__` in project_id. "None" stays a real
                   option: an opportunity deliberately assigned to no project is not the same
                   as one nobody has got to yet. */
                onChange={(v) => set('project_id', v === UNSET ? '' : v)}
                options={[
                  { value: '', label: 'None' },
                  ...projects.map((p) => ({
                    value: s(p.project_id),
                    id: s(p.project_id),
                    description: p.project_description ? s(p.project_description) : undefined,
                  })),
                ]}
              />
            </Field>
            <Field width="rest" label="Project Description">
              <ReadOnly
                matchControl
                value={projectDescription}
                fallback="Select a project to see its description"
              />
              {/*
               * UNLINK IS EDIT-ONLY — the owner's instruction. It is not rendered at all
               * in View, so there is no disabled control to click by mistake, and it only
               * appears once there is a project to unlink from.
               */}
              {!isNew && form.project_id && canUnlink ? (
                <button
                  type="button"
                  className="opp-btn secondary"
                  style={{ marginTop: 10, padding: '8px 16px' }}
                  disabled={busy}
                  onClick={() => void unlinkProject()}
                >
                  <i className="material-icons">link_off</i>
                  Remove from project
                </button>
              ) : null}
            </Field>
          </Row>
        ) : (
          <Row>
            <Field width="quarter" label="Project ID">
              <ReadOnly value={s(opp?.project_id)} fallback="Not assigned" />
            </Field>
            <Field width="three-quarters" label="Project Description">
              <ReadOnly value={s(opp?.project_description)} />
            </Field>
          </Row>
        )}
        {/*
         * THE FRAMEWORK PICKER — NOT IN THE SOURCE FORM, AND KEPT DELIBERATELY.
         *
         * The source's form has no framework control at all; the one in this product lived
         * on the separate detail screen, which this screen has now absorbed. Dropping it
         * with that screen would have removed the ONLY way to give a standalone
         * opportunity a framework — and an opportunity without one cannot be scored, which
         * is exactly the "This opportunity has no framework assigned" dead end on the
         * scoring form. So it is carried over rather than lost (CLAUDE.md, "Additive
         * change only"). Logged in docs/DEVIATIONS.md.
         *
         * The server decides when it may be shown: `availableFrameworks` comes back empty
         * once the opportunity belongs to a project, because the project's governance owns
         * the framework from then on.
         */}
        {full && availableFrameworks.length ? (
          <Row>
            <Field
              width="full"
              label="Scoring Framework"
              helper="This opportunity has no project, so its framework can be chosen here. Once it joins a project, the project's framework applies."
            >
              <select
                className="opp-form-select"
                value={s(opp?.framework_id)}
                disabled={busy}
                onChange={(e) => void assignFramework(e.target.value)}
              >
                <option value="">— choose a framework —</option>
                {availableFrameworks.map((f) => (
                  <option key={s(f.framework_id)} value={s(f.framework_id)}>
                    {s(f.framework_name)}
                  </option>
                ))}
              </select>
            </Field>
          </Row>
        ) : null}
      </Card>

      <Card icon="people" title="Stakeholders">
        {full ? (
          <>
            <Row>
              <Field
                width="half"
                label="Opportunity Owner"
                required
                helper="Email for notifications"
              >
                <input
                  type="text"
                  className="opp-form-input"
                  maxLength={100}
                  placeholder="Enter owner name"
                  value={form.owner_name}
                  onChange={(e) => set('owner_name', e.target.value)}
                />
                <input
                  type="email"
                  className="opp-form-input"
                  maxLength={100}
                  placeholder="Enter owner email (for notifications)"
                  style={{ marginTop: 8 }}
                  value={form.opportunity_owner_email}
                  onChange={(e) => set('opportunity_owner_email', e.target.value)}
                />
              </Field>
              <Field width="half" label="Business Process Owner" helper="Email for notifications">
                <input
                  type="text"
                  className="opp-form-input"
                  maxLength={100}
                  placeholder="Enter business process owner name"
                  value={form.business_process_owner}
                  onChange={(e) => set('business_process_owner', e.target.value)}
                />
                <input
                  type="email"
                  className="opp-form-input"
                  maxLength={100}
                  placeholder="Enter BPO email (for notifications)"
                  style={{ marginTop: 8 }}
                  value={form.bpo_email}
                  onChange={(e) => set('bpo_email', e.target.value)}
                />
              </Field>
            </Row>
            <Row>
              {/* The third scoring owner. The questionnaire is routed by aspect — Technical to
                  the Opportunity Owner, Business to the BPO, Strategy to this person — and an
                  aspect with no email cannot be invited, so this pair is what makes the
                  Strategy dimensions answerable at all.

                  Kept SEPARATE from Sponsor / Key Stakeholder, which carries no email and is
                  left exactly as it was. The two are plausibly the same person; deciding that
                  they are is the owner's call, not an inference to act on here. */}
              <Field
                width="half"
                label="Executive Owner"
                helper="Answers the Strategy questions. Email required to invite them."
              >
                <input
                  type="text"
                  className="opp-form-input"
                  maxLength={100}
                  placeholder="Enter executive owner name"
                  value={form.executive_owner}
                  onChange={(e) => set('executive_owner', e.target.value)}
                />
                <input
                  type="email"
                  className="opp-form-input"
                  maxLength={100}
                  placeholder="Enter executive owner email (for notifications)"
                  style={{ marginTop: 8 }}
                  value={form.executive_owner_email}
                  onChange={(e) => set('executive_owner_email', e.target.value)}
                />
              </Field>
              <Field width="half" label="Sponsor / Key Stakeholder" helper="Email for notifications">
                <input
                  type="text"
                  className="opp-form-input"
                  maxLength={100}
                  placeholder="Enter sponsor/key stakeholder name"
                  value={form.sponsor_keystakeholder}
                  onChange={(e) => set('sponsor_keystakeholder', e.target.value)}
                />
                {/* Added 2026-09-21 at the owner's request: the same contact line the other
                    three roles carry. */}
                <input
                  type="email"
                  className="opp-form-input"
                  maxLength={100}
                  placeholder="Enter sponsor/key stakeholder email (for notifications)"
                  style={{ marginTop: 8 }}
                  value={form.sponsor_keystakeholder_email}
                  onChange={(e) => set('sponsor_keystakeholder_email', e.target.value)}
                />
              </Field>
            </Row>
          </>
        ) : (
          /* View is the edit screen frozen (owner ruling), so it shows the same four roles
             and ALWAYS shows the e-mail line — an empty one reads as N/A rather than
             vanishing, which is how the field looked absent when a record had none
             (2026-09-21). */
          <>
            <Row>
              <Field width="half" label="Opportunity Owner" helper="Email for notifications">
                <ReadOnly value={s(opp?.owner_name)} />
                <ReadOnly value={s(opp?.opportunity_owner_email)} />
              </Field>
              <Field width="half" label="Business Process Owner" helper="Email for notifications">
                <ReadOnly value={s(opp?.business_process_owner)} />
                <ReadOnly value={s(opp?.bpo_email)} />
              </Field>
            </Row>
            <Row>
              <Field
                width="half"
                label="Executive Owner"
                helper="Answers the Strategy questions. Email required to invite them."
              >
                <ReadOnly value={s(opp?.executive_owner)} />
                <ReadOnly value={s(opp?.executive_owner_email)} />
              </Field>
              <Field width="half" label="Sponsor / Key Stakeholder" helper="Email for notifications">
                <ReadOnly value={s(opp?.sponsor_keystakeholder)} />
                <ReadOnly value={s(opp?.sponsor_keystakeholder_email)} />
              </Field>
            </Row>
          </>
        )}
      </Card>
    </>
  );

  /* ────────────────────────────────────────────────────────────────────────────────
   * TAB 3 — BUSINESS VALUE.  source :889–1121
   * ──────────────────────────────────────────────────────────────────────────────── */
  const businessPane = (
    <>
      <Card icon="attach_money" title="Financial Details">
        {full ? (
          <Row>
            <Field
              width="half"
              label="Annual Business Benefit ($)"
              helper="Expected annual business benefit in dollars"
            >
              <MoneyInput
                value={form.business_benefit}
                onChange={(v) => set('business_benefit', v)}
              />
            </Field>
            <Field
              width="half"
              label="Estimated Delivery Cost ($)"
              helper="Estimated cost to deliver this opportunity in dollars"
            >
              <MoneyInput
                value={form.estimated_delivery_cost}
                onChange={(v) => set('estimated_delivery_cost', v)}
              />
            </Field>
          </Row>
        ) : (
          <Row>
            <Field width="half" label="Annual Business Benefit">
              <ReadOnly value={money0(opp?.business_benefit)} money />
            </Field>
            <Field width="half" label="Estimated Delivery Cost">
              <ReadOnly value={money0(opp?.estimated_delivery_cost)} money />
            </Field>
          </Row>
        )}
      </Card>

      <Card icon="assessment" title="Business Assessment">
        {full ? (
          <>
            <Row>
              <Field width="half" label="Business Criticality">
                <select
                  className="opp-form-select"
                  value={form.criticality_score}
                  onChange={(e) => set('criticality_score', e.target.value)}
                >
                  <option value="">Select criticality level</option>
                  {rubric('criticalityLevels').map((l) => (
                    <option key={l.score} value={String(l.score)}>
                      Score {l.score} - {l.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                width="half"
                label="Strategic Alignment"
                helper="How closely this aligns with the organization's Strategy"
              >
                <select
                  className="opp-form-select"
                  value={form.ai_alignment_score}
                  onChange={(e) => set('ai_alignment_score', e.target.value)}
                >
                  <option value="">Select strategic alignment level</option>
                  {rubric('aiAlignmentLevels').map((l) => (
                    <option key={l.score} value={String(l.score)}>
                      Score {l.score} - {l.label}
                    </option>
                  ))}
                </select>
              </Field>
            </Row>
            <Row>
              <Field
                width="half"
                label="Benefit / Cost Effectiveness"
                helper="Expected return on investment (ROI) and payback period"
              >
                <select
                  className="opp-form-select"
                  value={form.benefit_cost_effectiveness_score}
                  onChange={(e) => set('benefit_cost_effectiveness_score', e.target.value)}
                >
                  <option value="">Select benefit/cost effectiveness level</option>
                  {rubric('benefitCostLevels').map((l) => (
                    <option key={l.score} value={String(l.score)}>
                      Score {l.score} - {l.label}
                      {l.description ? ` (${l.description})` : ''}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                width="half"
                label="Delivery Risk"
                helper="Assessment of technical, resource, and stakeholder risks"
              >
                <select
                  className="opp-form-select"
                  value={form.delivery_risk_score}
                  onChange={(e) => set('delivery_risk_score', e.target.value)}
                >
                  <option value="">Select delivery risk level</option>
                  {rubric('deliveryRiskLevels').map((l) => (
                    <option key={l.score} value={String(l.score)}>
                      Score {l.score} - {l.label}
                      {l.description ? ` (${l.description})` : ''}
                    </option>
                  ))}
                </select>
              </Field>
            </Row>
            <Row>
              <Field
                width="half"
                label="Mandatory for Portfolio Optimization"
                helper='When enabled, this opportunity will be automatically selected when "Respect Mandatory" constraint is active'
              >
                <div className="opp-checkbox-wrapper">
                  <input
                    type="checkbox"
                    id="is_mandatory"
                    checked={form.is_mandatory}
                    onChange={(e) => set('is_mandatory', e.target.checked)}
                  />
                  <label htmlFor="is_mandatory">
                    Mark as mandatory (will be automatically included in portfolio optimization)
                  </label>
                </div>
              </Field>
              <Field
                width="half"
                label="NPV Value Strength"
                helper="Relative financial attractiveness based on NPV calculations"
              >
                <select
                  className="opp-form-select"
                  value={form.npv_value_strength}
                  onChange={(e) => set('npv_value_strength', e.target.value)}
                >
                  <option value="">Select NPV Value Strength</option>
                  {Object.entries(NPV_LABELS).map(([v, label]) => (
                    <option key={v} value={v}>
                      {v} - {label}
                    </option>
                  ))}
                </select>
              </Field>
            </Row>
          </>
        ) : (
          <>
            <Row>
              <Field width="half" label="Business Criticality">
                <ReadOnly
                  value={
                    opp?.criticality_label
                      ? `${s(opp.criticality_score)} - ${s(opp.criticality_label)}`
                      : ''
                  }
                />
              </Field>
              <Field width="half" label="Strategic Alignment">
                <ReadOnly
                  value={
                    opp?.ai_alignment_label
                      ? `${s(opp.ai_alignment_score)} - ${s(opp.ai_alignment_label)}`
                      : ''
                  }
                />
              </Field>
            </Row>
            <Row>
              <Field width="half" label="Benefit / Cost Effectiveness">
                <ReadOnly
                  value={
                    opp?.benefit_cost_effectiveness_label
                      ? `${s(opp.benefit_cost_effectiveness_score)} - ${s(opp.benefit_cost_effectiveness_label)}`
                      : ''
                  }
                />
              </Field>
              <Field width="half" label="Delivery Risk">
                <ReadOnly
                  value={
                    opp?.delivery_risk_label
                      ? `${s(opp.delivery_risk_score)} - ${s(opp.delivery_risk_label)}`
                      : ''
                  }
                />
              </Field>
            </Row>
            <Row>
              <Field width="half" label="Mandatory for Portfolio Optimization">
                <ReadOnly value={opp?.is_mandatory ? 'YES - Mandatory' : 'NO'} />
              </Field>
              <Field width="half" label="MVP Value Strength">
                <ReadOnly
                  value={
                    opp?.mvp_value_strength_label ? (
                      <>
                        {s(opp.mvp_value_strength_score)} - {s(opp.mvp_value_strength_label)}
                        {opp.mvp_value_strength_auto_calculated ? (
                          <span style={{ color: '#1976d2', fontSize: 11 }}> (Auto)</span>
                        ) : null}
                      </>
                    ) : (
                      ''
                    )
                  }
                />
              </Field>
            </Row>
            <Row>
              <Field width="half" label="NPV Value Strength">
                <ReadOnly
                  value={
                    opp?.npv_value_strength
                      ? `${s(opp.npv_value_strength)} - ${NPV_LABELS[s(opp.npv_value_strength)] ?? ''}`
                      : ''
                  }
                />
              </Field>
              {/* Source :1099 — empty, for layout balance. */}
              <Field width="half">
                <></>
              </Field>
            </Row>
            {opp?.mvp_value_strength_remark ? (
              <Row>
                <Field width="full" label="MVP Value Strength Remark">
                  <div className="opp-readonly-value" style={{ fontSize: 13 }}>
                    {s(opp.mvp_value_strength_remark)}
                  </div>
                </Field>
              </Row>
            ) : null}
          </>
        )}
      </Card>
    </>
  );

  /* ────────────────────────────────────────────────────────────────────────────────
   * TAB 4 — FINANCIAL MODEL.  source :1122–1268
   *
   * Note the source does NOT gate this tab on `canFullEdit` — the four run-rate inputs
   * render the same either way. That is reproduced; only View freezes them.
   * ──────────────────────────────────────────────────────────────────────────────── */
  const financialPane = (
    <Card icon="trending_up" title="MVP Financial Parameters">
      {!isNew ? (
        <div className="opp-notice info">
          <i className="material-icons">info</i>
          <span>
            These values feed into the financial model calculations (ROI, Payback Period, etc.).
            Also available in{' '}
            <a
              href={`/portfolio_definition/financial-modeling/value-roi/cost-scenarios?opportunity=${encodeURIComponent(
                opportunityId!,
              )}&project=${encodeURIComponent(s(opp?.project_id))}`}
            >
              Cost &amp; Scenarios
            </a>{' '}
            or{' '}
            <a
              href={`/portfolio_definition/financial-modeling/value-roi/financial-summary?opportunity=${encodeURIComponent(
                opportunityId!,
              )}&project=${encodeURIComponent(s(opp?.project_id))}`}
            >
              Financial Summary
            </a>{' '}
            pages.
          </span>
        </div>
      ) : null}

      <Row>
        <Field
          width="half"
          label="Current Run Rate ($/yr)"
          helper="Current annual operating cost before implementation"
        >
          <input
            type="number"
            className="opp-form-input"
            step="0.01"
            min="0"
            placeholder="0.00"
            disabled={readOnly}
            value={form.current_run_rate}
            onChange={(e) => set('current_run_rate', e.target.value)}
          />
        </Field>
        <Field
          width="half"
          label="Future Run Rate ($/yr)"
          helper="Expected annual operating cost after implementation"
        >
          <input
            type="number"
            className="opp-form-input"
            step="0.01"
            min="0"
            placeholder="0.00"
            disabled={readOnly}
            value={form.future_run_rate}
            onChange={(e) => set('future_run_rate', e.target.value)}
          />
        </Field>
      </Row>
      <Row>
        <Field
          width="half"
          label="Annual Hosting Cost ($/yr)"
          helper="Annual infrastructure/cloud hosting costs"
        >
          <input
            type="number"
            className="opp-form-input"
            step="0.01"
            min="0"
            placeholder="0.00"
            disabled={readOnly}
            value={form.annual_hosting_cost}
            onChange={(e) => set('annual_hosting_cost', e.target.value)}
          />
        </Field>
        <Field
          width="half"
          label="Annual Support Cost ($/yr)"
          helper="Annual maintenance and support costs"
        >
          <input
            type="number"
            className="opp-form-input"
            step="0.01"
            min="0"
            placeholder="0.00"
            disabled={readOnly}
            value={form.annual_support_cost}
            onChange={(e) => set('annual_support_cost', e.target.value)}
          />
        </Field>
      </Row>

      {/* Source :1180 — the calculated strip. These four are read-outs of the saved model,
          not of what is currently typed; the source recomputes them on save. */}
      <div className="opp-calc-strip">
        <div className="opp-calc-row">
          <div>
            <span className="opp-calc-label">Build Cost</span>
            <strong className="opp-calc-value build">
              {financialModel?.build_cost ? money0(financialModel.build_cost) : '$0'}
            </strong>
          </div>
          <div>
            <span className="opp-calc-label">Annual Savings</span>
            <strong className="opp-calc-value savings">
              {financialModel?.annual_savings ? money0(financialModel.annual_savings) : '$0'}
            </strong>
          </div>
          <div>
            <span className="opp-calc-label">ROI</span>
            <strong className="opp-calc-value roi">
              {financialModel?.roi_percent
                ? `${parseFloat(String(financialModel.roi_percent)).toFixed(1)}%`
                : '0.0%'}
            </strong>
          </div>
          <div>
            <span className="opp-calc-label">Payback</span>
            <strong className="opp-calc-value payback">
              {financialModel?.payback_years
                ? `${parseFloat(String(financialModel.payback_years)).toFixed(2)} yrs`
                : 'N/A'}
            </strong>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <Row>
          <Field
            width="half"
            label="MVP Value Strength"
            helper="How clearly defined and validated the MVP is. Can be auto-calculated from financial model data."
          >
            <select
              className="opp-form-select"
              disabled={!full}
              value={form.mvp_value_strength_score}
              onChange={(e) => set('mvp_value_strength_score', e.target.value)}
            >
              <option value="">Select MVP value strength level</option>
              {rubric('mvpValueStrengthLevels').map((l) => (
                <option key={l.score} value={String(l.score)}>
                  Score {l.score} - {l.label}
                </option>
              ))}
            </select>
          </Field>
          <Field width="half" label={<>&nbsp;</>}>
            {isNew ? (
              <span className="opp-form-helper" style={{ color: '#999', display: 'block', paddingTop: 12 }}>
                Save opportunity first to enable auto-calculation
              </span>
            ) : null}
          </Field>
        </Row>
      </div>

      {opp?.mvp_value_strength_remark ? (
        <Row>
          <Field width="full" label="MVP Value Strength Remark">
            <div className="opp-readonly-value" style={{ fontSize: 13 }}>
              {s(opp.mvp_value_strength_remark)}
              {opp.mvp_value_strength_auto_calculated ? (
                <span style={{ color: '#1976d2', marginLeft: 8 }}>(Auto-calculated)</span>
              ) : null}
            </div>
          </Field>
        </Row>
      ) : null}
    </Card>
  );

  /* ────────────────────────────────────────────────────────────────────────────────
   * TAB 5 — DOCUMENTATION.  source :1269–1330
   * The three link fields stay writable in limited-edit mode — that is the whole point
   * of that mode (source :6470).
   * ──────────────────────────────────────────────────────────────────────────────── */
  const DOCS: {
    field: keyof FormState;
    colour: string;
    icon: string;
    label: string;
    helper: string;
  }[] = [
    {
      field: 'initial_discussion_link',
      colour: '#4285f4',
      icon: '\u{1F4C4}',
      label: 'Initial Discussion / Observation',
      helper: 'Paste the link to the initial discussion document',
    },
    {
      field: 'business_process_requirements_link',
      colour: '#0f9d58',
      icon: '\u{1F4C8}',
      label: 'Business Process Requirements',
      helper: 'Paste the link to the business process requirements document',
    },
    {
      field: 'technical_requirements_link',
      colour: '#f4b400',
      icon: '\u{1F4BB}',
      label: 'Technical Requirements',
      helper: 'Paste the link to the technical requirements document',
    },
  ];

  const documentsPane = (
    <Card
      icon="description"
      title="Documentation"
      /* Source :1287 — `partials/info-tip`, id docInfo. Copy verbatim. */
      tip={
        <InfoTip title="Documentation">
          <p>Link documents to this opportunity by pasting the <strong>full URL</strong> to each document.</p>
          <ul>
            <li><strong>Any source works</strong> — any cloud storage or document-management system. Just paste the shareable link.</li>
            <li>Make sure the people who need it have <strong>access</strong> to the link before sharing.</li>
            <li>Add links for the <strong>Initial Discussion / Observation</strong>, <strong>Business Process Requirements</strong>, and <strong>Technical Requirements</strong> as they become available.</li>
          </ul>
        </InfoTip>
      }
    >
      {DOCS.map((d) => (
        <Row key={d.field}>
          <Field
            width="full"
            label={
              <>
                <span className="opp-doc-icon" style={{ color: d.colour }}>
                  {d.icon}
                </span>{' '}
                {d.label}
              </>
            }
            helper={d.helper}
          >
            {docsWritable ? (
              <input
                type="text"
                className="opp-form-input"
                placeholder="https://… paste the full document URL"
                value={String(form[d.field])}
                onChange={(e) => set(d.field, e.target.value)}
              />
            ) : String(form[d.field]) ? (
              <div className="opp-readonly-value">
                <a href={String(form[d.field])} target="_blank" rel="noreferrer">
                  {String(form[d.field])}
                </a>
              </div>
            ) : (
              <ReadOnly value="" fallback="No link added" />
            )}
          </Field>
        </Row>
      ))}
    </Card>
  );

  /* ────────────────────────────────────────────────────────────────────────────────
   * TAB 6 — NOTES.  source :1331–1374
   * The source's own help text says notes can be added "even when viewing an opportunity
   * in read-only mode", so the composer is NOT hidden in View.
   * ──────────────────────────────────────────────────────────────────────────────── */
  const notesPane = (
    <>
      <Card icon="note_add" title="Add Note">
        <Row>
          <Field width="full">
            <textarea
              className="opp-form-textarea"
              maxLength={400}
              rows={3}
              placeholder="Enter your note here (max 400 characters)..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <div className="opp-note-foot">
              <span className="opp-note-count">{noteText.length} / 400 characters</span>
              <button
                type="button"
                className="opp-btn primary"
                style={{ padding: '8px 16px' }}
                disabled={busy || !noteText.trim()}
                onClick={() => void addNote()}
              >
                <i className="material-icons">add</i>
                Add Note
              </button>
            </div>
          </Field>
        </Row>
      </Card>

      <Card icon="list" title="Notes History">
        <div className="opp-notes-list">
          {notes === null ? (
            <div className="opp-note-empty">Loading notes...</div>
          ) : notes.length === 0 ? (
            <div className="opp-note-empty">No notes yet</div>
          ) : (
            notes.map((n) => (
              <div className="opp-note" key={s(n.id)}>
                <div className="opp-note-meta">
                  <span>
                    <span className="opp-note-author">{s(n.created_by_name) || 'Unknown'}</span>
                    {' · '}
                    {stamp(n.created_at)}
                  </span>
                  <button
                    type="button"
                    className="opp-note-del"
                    title="Delete note"
                    disabled={busy}
                    onClick={() => void deleteNote(Number(n.id))}
                  >
                    ✕
                  </button>
                </div>
                <div className="opp-note-text">{s(n.note_text)}</div>
              </div>
            ))
          )}
        </div>
      </Card>
    </>
  );

  /* ────────────────────────────────────────────────────────────────────────────────
   * TAB 7 — SCORING DETAILS.  `views/opportunities-details.ejs`:1653–1748
   *
   * VIEW ONLY, and only when there is something to show — the source gates the tab on
   * `dimension_scores.length || (scoring_responses.length && status !== 'Scoring in
   * Progress')` (:1701) and gates each card on the same conditions again.
   * ──────────────────────────────────────────────────────────────────────────────── */
  const scoringPane = (
    <>
      {/* Says WHY there is no overall score, rather than leaving the reader to infer it. */}
      {!dimensionScores.length && questionScores.length ? (
        <Card icon="hourglass_top" title="Scoring in progress">
          <p className="opp-help" style={{ margin: 0 }}>
            {questionScores.length} answer{questionScores.length === 1 ? '' : 's'} recorded so far.
            The dimension scores and the overall score are calculated once every question has
            been answered, so they are not shown yet. The answers below are read-only — use
            <strong> Continue</strong> on the Scoring in Progress screen to change them.
          </p>
        </Card>
      ) : null}

      {dimensionScores.length ? (
        <Card icon="bar_chart" title="Dimension Scores">
          <table className="opp-scoring-table">
            <thead>
              <tr>
                <th style={{ width: '25%' }}>Dimension</th>
                <th style={{ width: '35%' }}>Description</th>
                <th style={{ width: '15%' }}>Score</th>
                <th style={{ width: '25%' }}>Performance</th>
              </tr>
            </thead>
            <tbody>
              {dimensionScores.map((d, i) => {
                const score = Number(d.dimension_score ?? 0);
                return (
                  <tr key={s(d.dimension_id) || i}>
                    <td>
                      <strong>{s(d.dimension_name)}</strong>
                    </td>
                    <td>{s(d.dimension_description)}</td>
                    <td>
                      <strong>{score.toFixed(2)}</strong> / 5.0
                    </td>
                    <td>
                      <div className="opp-perf">
                        <div
                          style={{
                            /* Source :1682 — the same three thresholds and colours. */
                            background: score >= 4 ? '#4caf50' : score >= 3 ? '#ffc107' : '#f44336',
                            width: `${(score / 5) * 100}%`,
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      ) : null}

      {/*
        The answers show while scoring is still in progress too — the same owner ruling that
        opened this tab for a part-scored opportunity. Hiding them here was the other half of
        the source's gate, and it made the tab render a note about answers "below" with
        nothing below it.
      */}
      {questionScores.length ? (
        <Card icon="question_answer" title="Detailed Scoring Responses">
          <div className="opp-scroll-x">
            <table className="opp-scoring-table">
              <thead>
                <tr>
                  <th style={{ textTransform: 'uppercase' }}>Dimension</th>
                  <th style={{ textTransform: 'uppercase' }}>Component</th>
                  <th style={{ textTransform: 'uppercase' }}>Question</th>
                  <th style={{ textAlign: 'center', textTransform: 'uppercase' }}>Weightage</th>
                  <th style={{ textAlign: 'center', textTransform: 'uppercase' }}>Answer</th>
                </tr>
              </thead>
              <tbody>
                {questionScores.map((r, i) => {
                  const w = Number(r.weightage ?? 0);
                  return (
                    <tr key={s(r.question_id) || i}>
                      {/*
                       * PORTED SOURCE DEFECT — this cell is always blank.
                       *
                       * The view renders `resp.dimension_name`, but the query that builds
                       * these rows (routes/index.js:7187) joins only `questions` and
                       * `components` — it never selects a dimension name. So the column has
                       * a header and no data in the source, and it does the same here.
                       * §1.2 rule 6: port it as-is, log it, raise it. See
                       * docs/DEVIATIONS.md, "Known source defects (ported intentionally)".
                       */}
                      <td>{s(r.dimension_name)}</td>
                      <td>{s(r.component_name)}</td>
                      <td>{s(r.question_text)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span
                          className="opp-badge"
                          style={{
                            /* Source :1717 — weightage 4+ red, 3+ orange, else grey. */
                            backgroundColor: w >= 4 ? '#f44336' : w >= 3 ? '#ff9800' : '#9e9e9e',
                            padding: '4px 10px',
                          }}
                        >
                          {w}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <strong>{s(r.answer_value)}</strong> / 5
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      {recommendation && s(opp?.status) === 'Scored' ? (
        <Card icon="lightbulb" title="Detailed Recommendations">
          <div className="opp-reco">{s(recommendation.recommendations)}</div>
        </Card>
      ) : null}
    </>
  );

  const panes: Record<OppTabKey, React.ReactNode> = {
    overview: overviewPane,
    ownership: ownershipPane,
    business: businessPane,
    financial: financialPane,
    documents: documentsPane,
    notes: notesPane,
    scoring: scoringPane,
  };

  /* ────────────────────────────────────────────────────────────────────────────────
   * Render.
   * ──────────────────────────────────────────────────────────────────────────────── */

  return (
    <div
      className="opp-screen"
      ref={setRootEl}
      style={rootH === null ? undefined : { height: `${rootH}px` }}
    >
      <div className="opp-fixed">
        <div className="pd-page-header">
          <div>
            <h1 className="pd-page-header__title">{heading}</h1>
            <p className="pd-page-header__subtitle">{subtitle}</p>
          </div>
          <div className="pd-page-header__actions">
            <button type="button" className="oh-launch" onClick={() => setHelpOpen((v) => !v)}>
              <span className="oh-q">?</span> How this works
            </button>
          </div>
        </div>

        {/* Source :444 — the status band, only once the opportunity exists. */}
        {!isNew ? (
          <div className="opp-status-header">
            <div className="status-info">
              <span>Status:</span>
              <span className={`opp-status-chip ${statusClass(s(opp?.status))}`}>
                {s(opp?.status)}
              </span>
            </div>
            {!canFullEdit && !readOnly ? (
              <div className="opp-notice" style={{ margin: 0, flex: 1, maxWidth: 500 }}>
                <i className="material-icons">info</i>
                <span>
                  This opportunity is in "{s(opp?.status)}" status. Only Cloud Document links can
                  be edited.
                </span>
              </div>
            ) : null}
            {/*
             * VIEW'S ACTIONS SIT IN THE STATUS BAND, not the bottom bar — that is where
             * `opportunities-details.ejs`:1668–1687 puts them, in `.opp-action-buttons`
             * opposite the status chip. Both conditions below are the source's.
             */}
            {readOnly ? (
              <div className="opp-action-buttons">
                {/* Approved AND has a framework → live (:1669). Draft or On Hold → shown
                    disabled, carrying the source's own explanation (:1677). Any other
                    status → the button is absent entirely. */}
                {s(opp?.status) === 'Approved' && opp?.framework_id ? (
                  <button
                    type="button"
                    className="opp-btn success"
                    disabled={busy}
                    onClick={() => void submitToScore()}
                  >
                    <i className="material-icons">send</i>
                    Submit to Score
                  </button>
                ) : null}
                {['Draft', 'On Hold'].includes(s(opp?.status)) ? (
                  <button
                    type="button"
                    className="opp-btn success"
                    disabled
                    title="Only Approved opportunities can be submitted to scoring. Approve this opportunity first."
                    style={{ opacity: 0.4, cursor: 'not-allowed' }}
                  >
                    <i className="material-icons">send</i>
                    Submit to Score
                  </button>
                ) : null}
                {/* Edit disappears once the record is Committed or beyond (:1681). */}
                {!['Committed', 'In Development', 'Delivered'].includes(s(opp?.status)) ? (
                  <button
                    type="button"
                    className="opp-btn primary"
                    onClick={() =>
                      navigate(`${REGISTRY}/${encodeURIComponent(opportunityId!)}/edit`)
                    }
                  >
                    <i className="material-icons">edit</i>
                    Edit
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="opp-tab-container">
          <div className="opp-tabs">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`opp-tab-button${tab === t.key ? ' active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                <span className="tab-icon">{t.icon}</span>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="opp-scroll">
        {error ? (
          <div className="opp-flash opp-flash-error">
            <div>
              <i className="material-icons">error_outline</i>
              <span>{error}</span>
            </div>
          </div>
        ) : null}
        {success ? (
          <div className="opp-flash opp-flash-success">
            <div>
              <i className="material-icons">check_circle</i>
              <span>{success}</span>
            </div>
          </div>
        ) : null}

        {tabs.map((t) => (
          <div
            key={t.key}
            className={`opp-tab-content${tab === t.key ? ' active' : ''}`}
            id={`opp-content-${t.key}`}
          >
            {panes[t.key]}
          </div>
        ))}

        {/* Source :1430 — the action bar, always visible at the bottom. */}
        <div className="opp-card" style={{ marginTop: 20 }}>
          <div className="opp-form-actions">
            {!readOnly ? (
              <button
                type="button"
                className="opp-btn primary"
                disabled={busy}
                onClick={() => void save()}
              >
                <i className="material-icons">save</i>
                {canFullEdit
                  ? isNew
                    ? 'Create Opportunity'
                    : 'Update Opportunity'
                  : 'Update Document Links'}
              </button>
            ) : null}

            {/* "Back to Registry" was removed 2026-09-21 (owner-directed): the breadcrumb is
                the way back. Cancel stays, because discarding an edit is a different act. */}
            {!readOnly ? (
              <button type="button" className="opp-btn secondary" onClick={() => navigate(REGISTRY)}>
                <i className="material-icons">cancel</i>
                Cancel
              </button>
            ) : null}


            {/* Edit only. In View the source's own read-only stance applies: no lifecycle
                actions, because none of them are reversible from here. */}
            {!readOnly && !isNew && form.framework_relevant !== '0' ? (
              <button
                type="button"
                className="opp-btn success"
                disabled={busy}
                onClick={() => void submitToScore()}
              >
                <i className="material-icons">send</i>
                Submit to Score
              </button>
            ) : null}

            {!readOnly && !isNew && s(opp?.status) === 'Approved' ? (
              <button
                type="button"
                className="opp-btn warning"
                disabled={busy}
                onClick={() => void revertToDraft()}
              >
                <i className="material-icons">undo</i>
                Revert to Draft
              </button>
            ) : null}

            {!readOnly && full && !isNew && s(opp?.status) === 'Draft' ? (
              <button
                type="button"
                className="opp-btn danger right"
                disabled={busy}
                onClick={() => void remove()}
              >
                <i className="material-icons">delete</i>
                Delete
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <OpportunityFormHelp tab={tab} open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
